import { json, LoaderFunctionArgs, type MetaFunction } from "@remix-run/node";
import {
  isRouteErrorResponse,
  Link,
  Outlet,
  useLoaderData,
  useRevalidator,
  useRouteError,
} from "@remix-run/react";
import { useEffect, useRef } from "react";
import stylesheet from "../tailwind.css?url";
import {
  getSupabaseWithSessionHeaders,
  supabaseAdmin,
} from "~/lib/supabase_server";
import StoreHeader from "../components/Storeheader";
import { StoreContext } from "~/hooks/use-store";
import { Product, StoreAddress } from "~/lib/types";
import type { PublicStoreProfileRow } from "~/lib/public_store_profiles";
import { PUBLIC_STORE_PROFILES } from "~/lib/public_store_profiles";
import { reportRouteError } from "~/lib/sentryReport";
import { StoreReviewsProps } from "~/components/admin/store/StoreReviews";
import {
  STORE_REVIEW_FALLBACK_DISPLAY_NAME,
  STORE_REVIEW_FALLBACK_INITIALS,
} from "~/lib/store_review_display";
import { isPublicStoreSlugParam, slugify } from "../lib/helpers/storehelpers";
import { resolveStoreFeaturedProducts } from "~/lib/store_featured_products.server";

type LoaderData = {
  storeProducts: Product[];
  storeDetails: PublicStoreProfileRow;
  storeReviews: StoreReviewsProps[];
  storeAddress: StoreAddress | null;
  featuredProducts: Product[];
};

/** Store availability can change at any time; avoid caching document HTML. */
function mergeStoreLoaderHeaders(base: Headers): Headers {
  const out = new Headers(base);
  out.set("Cache-Control", "private, max-age=0, must-revalidate");
  return out;
}

export async function loader({ params, request }: LoaderFunctionArgs) {
  const { headers, supabase } = await getSupabaseWithSessionHeaders({
    request,
  });

  const storeName = params.id || "";
  if (!storeName) {
    throw new Response("Store name is required", {
      status: 400,
      headers: mergeStoreLoaderHeaders(headers),
    });
  }

  if (!isPublicStoreSlugParam(storeName)) {
    throw json({ code: "STORE_NOT_FOUND" as const }, {
      status: 404,
      headers: mergeStoreLoaderHeaders(headers),
    });
  }

  const { data, error } = await supabase
    .from(PUBLIC_STORE_PROFILES)
    .select("*")
    .ilike("slug", storeName)
    .single();

  if (error || !data) {
    const { data: storeRow } = await supabaseAdmin
      .from("Stores")
      .select("id, name, slug, status")
      .ilike("slug", storeName)
      .maybeSingle();

    if (!storeRow) {
      throw json({ code: "STORE_NOT_FOUND" as const }, {
        status: 404,
        headers: mergeStoreLoaderHeaders(headers),
      });
    }

    if (storeRow.status === "INACTIVE") {
      throw json(
        {
          code: "STORE_INACTIVE" as const,
          storeName: storeRow.name ?? "This store",
        },
        { status: 410, headers: mergeStoreLoaderHeaders(headers) },
      );
    }

    if (storeRow.status === "INCOMPLETE") {
      throw json({ code: "STORE_INCOMPLETE" as const }, {
        status: 404,
        headers: mergeStoreLoaderHeaders(headers),
      });
    }

    throw json({ code: "STORE_NOT_FOUND" as const }, {
      status: 404,
      headers: mergeStoreLoaderHeaders(headers),
    });
  }

  const storeId = data.id;
  let storeProducts: Product[] = [];

  // Public catalog: ACTIVE only, scoped by store resolved above.
  // Use service role so logged-in users see the same grid as guests (session JWT
  // on SSR often does not satisfy Products RLS the same way as anon).
  const { data: products, error: productsError } = await supabaseAdmin
    .from("Products")
    .select("*")
    .eq("store_id", storeId)
    .eq("status", "ACTIVE");

  if (products && !productsError) {
    storeProducts = products;
  }

  const storeReviews: StoreReviewsProps[] = [];
  const { data: reviews, error: reviewsError } = await supabase
    .from("StoreReviews")
    .select(
      "id, rating, comment, created_at, reviewer_display_name, reviewer_initials",
    )
    .eq("store_id", storeId);
  if (reviews && !reviewsError && reviews.length > 0) {
    for (const review of reviews) {
      storeReviews.push({
        id: review.id,
        rating: review.rating,
        comment: review.comment,
        created_at: review.created_at,
        reviewer_display_name:
          review.reviewer_display_name ?? STORE_REVIEW_FALLBACK_DISPLAY_NAME,
        reviewer_initials:
          review.reviewer_initials ?? STORE_REVIEW_FALLBACK_INITIALS,
      });
    }
  }

  // Fetch store address
  let storeAddress: StoreAddress | null = null;
  const { data: addressData, error: addressError } = await supabase
    .from("StoreAddresses")
    .select("*")
    .eq("store_id", storeId)
    .single();

  if (addressData && !addressError) {
    storeAddress = addressData;
  }

  const featuredProducts = await resolveStoreFeaturedProducts(
    supabaseAdmin,
    storeId,
    storeProducts,
  );

  return json(
    {
      storeReviews,
      storeProducts,
      storeDetails: data,
      storeAddress,
      featuredProducts,
    },
    { headers: mergeStoreLoaderHeaders(headers) },
  );
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data?.storeDetails) {
    return [{ title: "Store not found — BulkBuyPlace" }];
  }
  const name = data.storeDetails.name || "Store";
  const summary = data.storeDetails.summary
    ? String(data.storeDetails.summary).slice(0, 155)
    : `Shop wholesale products from ${name} on BulkBuyPlace.`;
  const title = `${name} — Wholesale supplier | BulkBuyPlace`;
  const image = data.storeDetails.banner_pic
    ? String(data.storeDetails.banner_pic).replaceAll('"', "")
    : null;

  return [
    { title },
    { name: "description", content: summary },
    { property: "og:title", content: title },
    { property: "og:description", content: summary },
    { property: "og:type", content: "website" },
    ...(image ? [{ property: "og:image", content: image }] : []),
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: summary },
    ...(image ? [{ name: "twitter:image", content: image }] : []),
  ];
};

export default function StorePageRoute() {
  const { revalidate } = useRevalidator();
  const revalidateRef = useRef(revalidate);
  revalidateRef.current = revalidate;
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        revalidateRef.current();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  const {
    storeProducts,
    storeDetails,
    storeReviews,
    storeAddress,
    featuredProducts,
  } = useLoaderData<typeof loader>() as LoaderData;

  const bannerPic = storeDetails.banner_pic?.replaceAll('"', "");
  const storeInitials = storeDetails.name
    ?.trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase())
    .join("") || "?";

  return (
    <StoreContext.Provider
      value={{
        storeDetails,
        storeProducts,
        storeReviews,
        storeAddress,
        featuredProducts,
      }}
    >
      <div className="mt-16 flex flex-1 flex-col">
        <div className="w-full h-[350px] relative flex shrink-0 items-center justify-center overflow-hidden">
          {storeDetails.banner_pic
            ? (
              <>
                <div className="absolute inset-0">
                  <img
                    src={bannerPic}
                    alt={storeDetails.name}
                    className="w-full h-full object-cover opacity-60"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/30" />
                </div>
                <Link
                  className="relative z-10 h-[250px] max-w-4xl w-full mx-auto px-4"
                  to={`/store/${slugify(storeDetails.name)}/home`}
                >
                  <img
                    src={bannerPic}
                    alt={storeDetails.name}
                    className="h-full w-full object-contain rounded-md shadow-lg"
                    loading="lazy"
                  />
                </Link>
              </>
            )
            : (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-slate-100 via-orange-50/50 to-slate-200 px-6">
                <div
                  className="pointer-events-none absolute inset-0 opacity-[0.35]"
                  style={{
                    backgroundImage:
                      "radial-gradient(circle at 1px 1px, rgb(148 163 184 / 0.35) 1px, transparent 0)",
                    backgroundSize: "24px 24px",
                  }}
                />
                <Link
                  className="relative z-[1] flex h-20 w-20 items-center justify-center rounded-2xl bg-white shadow-lg ring-1 ring-gray-200/80"
                  to={`/store/${slugify(storeDetails.name)}/home`}
                >
                  <span className="text-2xl font-bold text-orange-600">
                    {storeInitials}
                  </span>
                </Link>
                <p className="relative z-[1] mt-4 text-xs font-medium uppercase tracking-wide text-gray-500">
                  Supplier storefront
                </p>
              </div>
            )}
        </div>
        <StoreHeader />
        <div className="flex flex-1 flex-col bg-gray-100 dark:bg-gray-800">
          <Outlet />
        </div>
      </div>
    </StoreContext.Provider>
  );
}

function storeRouteErrorPayload(
  error: unknown,
): { code: string; storeName?: string } | null {
  if (!isRouteErrorResponse(error)) return null;
  const d = error.data;
  if (d && typeof d === "object" && !Array.isArray(d) && "code" in d) {
    return d as { code: string; storeName?: string };
  }
  return null;
}

export function ErrorBoundary() {
  const error = useRouteError();
  reportRouteError(error);
  const payload = storeRouteErrorPayload(error);

  if (isRouteErrorResponse(error)) {
    if (error.status === 410 && payload?.code === "STORE_INACTIVE") {
      return (
        <html lang="en">
          <head>
            <meta charSet="utf-8" />
            <meta
              name="viewport"
              content="width=device-width, initial-scale=1"
            />
            <title>Store unavailable</title>
            <link rel="stylesheet" href={stylesheet} />
          </head>
          <body className="min-h-dvh bg-muted text-foreground antialiased">
            <div className="flex min-h-dvh flex-col items-center justify-center px-6 py-16">
              <div className="w-full max-w-md rounded-xl border bg-card px-8 py-10 text-center shadow-sm">
                <h1 className="text-xl font-semibold tracking-tight">
                  This store is not available
                </h1>
                {payload?.storeName
                  ? (
                    <p className="mt-2 text-sm text-muted-foreground">
                      {payload.storeName} is not accepting orders on BulkBuyPlace
                      right now.
                    </p>
                  )
                  : null}
                <p className="mt-4 text-sm text-muted-foreground">
                  The owner may have deactivated this storefront. You can browse
                  other suppliers from the home page.
                </p>
                <Link
                  to="/"
                  className="mt-6 inline-flex items-center justify-center rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700"
                >
                  Go to home
                </Link>
              </div>
            </div>
          </body>
        </html>
      );
    }

    if (error.status === 404) {
      const isIncomplete = payload?.code === "STORE_INCOMPLETE";
      return (
        <html lang="en">
          <head>
            <meta charSet="utf-8" />
            <meta
              name="viewport"
              content="width=device-width, initial-scale=1"
            />
            <title>
              {isIncomplete ? "Store not yet available" : "Store not found"}
            </title>
            <link rel="stylesheet" href={stylesheet} />
          </head>
          <body className="min-h-dvh bg-background text-foreground antialiased">
            <div className="flex min-h-dvh flex-col items-center justify-center px-6 py-16 text-center">
              {isIncomplete
                ? (
                  <>
                    <h1 className="text-2xl font-bold text-red-600">
                      Oops! Something went wrong.
                    </h1>
                    <p className="mt-4 text-lg font-semibold">
                      Status Code: 404
                    </p>
                    <p className="mt-2 text-sm font-medium text-foreground">
                      Store not found
                    </p>
                    <p className="mt-3 max-w-md text-muted-foreground">
                      This store is not open yet. It may still be awaiting
                      confirmation before it appears on BulkBuyPlace.
                    </p>
                    <Link
                      to="/"
                      className="mt-8 inline-flex rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700"
                    >
                      Go to home
                    </Link>
                  </>
                )
                : (
                  <>
                    <h1 className="text-2xl font-semibold tracking-tight">
                      Store not found
                    </h1>
                    <p className="mt-3 max-w-md text-muted-foreground">
                      We could not find a store at this link. Check the URL or
                      return to the home page.
                    </p>
                    <Link
                      to="/"
                      className="mt-8 inline-flex rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700"
                    >
                      Go to home
                    </Link>
                  </>
                )}
            </div>
          </body>
        </html>
      );
    }
  }

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1"
        />
        <title>Something went wrong</title>
        <link rel="stylesheet" href={stylesheet} />
      </head>
      <body className="min-h-dvh bg-background px-6 py-16 text-center">
        <h1 className="text-xl font-semibold">Something went wrong</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {error instanceof Error ? error.message : "Please try again later."}
        </p>
        <Link
          to="/"
          className="mt-6 inline-block text-orange-600 hover:underline"
        >
          Go to home
        </Link>
      </body>
    </html>
  );
}
